import { Fragment, useState } from "react";
import { defaultObjectives, groupedItems, initialState, locations, objectives, Task } from "./CryoTypes";
import { CryoButton } from "./CryoButton";
import { isComplete } from "./isComplete";
import { Tooltip } from "react-tooltip";
import { HomeButton } from "./HomeButton";

export const CryoTracker = () => {
  const [tracker, setTracker] = useState(initialState);
  const [selectedObjectives, setSelectedObjectives] = useState(defaultObjectives);
  const [scale, setScale] = useState(100);
  const [colorKeyMode, setColorKeyMode] = useState(false);
  const [showObjectives, setShowObjectives] = useState(true);
  const [showLocations, setShowLocations] = useState(true);
  const [showItems, setShowItems] = useState(true);
  const [showTitles, setShowTitles] = useState(true);
  const [showMaps, setShowMaps] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [color, setColor] = useState("#a244aa");

  return (
    <div
      className="cryo-tracker"
      style={{
        backgroundColor: colorKeyMode ? color : "black",
      }}
    >
      <HomeButton />
      <Tooltip id="my-tooltip" className="tooltip" />
      <div className="heading">
        <h1>Cryo Tracker</h1>
        <p>A customizable, stream-friendly web app for tracking items, locations, and objectives for CRYO.</p>
        <button
          type="button"
          className="unstyled-button"
          onClick={() => {
            setShowSettings(!showSettings);
          }}
        >
          {showSettings ? "Hide" : "Settings"}
        </button>
        {showSettings && (
          <>
            <div className="settings">
              <button
                type="button"
                onClick={() => {
                  setTracker(initialState);
                  setSelectedObjectives(defaultObjectives);
                }}
              >
                Reset Tracker
              </button>
              <button type="button" onClick={() => setColorKeyMode(!colorKeyMode)}>
                Turn {colorKeyMode ? "Off" : "On"} Color Key
              </button>
            </div>
            <div className="settings">
              <button
                type="button"
                onClick={() => {
                  setShowItems(!showItems);
                }}
              >
                {showItems ? "Hide" : "Show"} Items
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowLocations(!showLocations);
                }}
              >
                {showLocations ? "Hide" : "Show"} Locations
              </button>
            </div>
            <div className="settings">
              <button
                type="button"
                onClick={() => {
                  setShowObjectives(!showObjectives);
                }}
              >
                {showObjectives ? "Hide" : "Show"} Objectives
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowTitles(!showTitles);
                }}
              >
                {showTitles ? "Hide" : "Show"} Titles
              </button>
            </div>
            <div className="settings">
              <button
                type="button"
                onClick={() => {
                  setShowMaps(!showMaps);
                }}
              >
                {showMaps ? "Hide" : "Show"} Maps
              </button>
            </div>
            <label htmlFor="scale-slider">Scale</label>
            <input
              className="scale-slider"
              id="scale-slider"
              type="range"
              min="50"
              max="100"
              value={scale}
              onChange={(e) => setScale(Number(e.target.value))}
            />
            <label htmlFor="color-key">Color Key Color</label>
            <input
              className="color-key"
              id="color-key"
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
            />
          </>
        )}

        <h3>Delta 7</h3>
      </div>
      <div
        className="content"
        style={{
          transform: `scale(${scale / 100})`,
        }}
      >
        {showItems && (
          <>
            {showTitles ? (
              <h3>Items</h3>
            ) : (
              <>
                <br />
                <br />
              </>
            )}
            <div className="flex-row">
              {groupedItems.map((group) => (
                <div className="flex-column pad-r-8" key={`group-${group.name}`}>
                  {group.items.map((item) => (
                    <div key={`group-${group.name}-item-${item}`}>
                      <CryoButton
                        task={item}
                        setValue={(key, value) => setTracker({ ...tracker, [key]: value })}
                        value={tracker[item]}
                        image
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}
        {showLocations && (
          <>
            {showTitles ? (
              <h3>Locations</h3>
            ) : (
              <>
                <br />
                <br />
              </>
            )}
            <div className="flex-row locations">
              {locations.map((location) => (
                <div className="pad-r-8" key={`location-${location}`}>
                  <CryoButton
                    task={location}
                    setValue={(key, value) => setTracker({ ...tracker, [key]: value })}
                    value={tracker[location]}
                  />
                </div>
              ))}
            </div>
          </>
        )}
        {showObjectives && (
          <>
            {showTitles ? (
              <h3>Objectives</h3>
            ) : (
              <>
                <br />
                <br />
              </>
            )}
            <div className="flex-row">
              {objectives.slice(0, 4).map((objective) => (
                <div className="pad-r-8">
                  <button
                    className={`cryo-button${selectedObjectives[objective.name] ? " active" : ""}`}
                    type="button"
                    onClick={() =>
                      setSelectedObjectives({
                        ...selectedObjectives,
                        [objective.name]: !selectedObjectives[objective.name],
                      })
                    }
                    key={`select-objective-${objective.name}`}
                  >
                    {objective.name}
                  </button>
                </div>
              ))}
            </div>

            <div className="flex-row">
              {objectives.slice(4, 7).map((objective) => (
                <div className="pad-r-8">
                  <button
                    className={`cryo-button${selectedObjectives[objective.name] ? " active" : ""}`}
                    type="button"
                    onClick={() =>
                      setSelectedObjectives({
                        ...selectedObjectives,
                        [objective.name]: !selectedObjectives[objective.name],
                      })
                    }
                    key={`select-objective-${objective.name}`}
                  >
                    {objective.name}
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
        {showObjectives &&
          objectives.map((objective) => (
            <Fragment key={`objective-${objective.name}`}>
              {selectedObjectives[objective.name] && (
                <>
                  <h3 style={{ fontStyle: isComplete(objective, tracker) ? "italic" : "" }}>
                    {objective.name}
                    {isComplete(objective, tracker) ? " Complete!" : ""}
                  </h3>
                  <div className="objective-section">
                    {objective.tasks.map((task) => (
                      <div className="flex-column" key={`task-${objective.name}-${task.flat().join("-")}`}>
                        {task.map(
                          (subTasks, index) =>
                            (!isComplete(objective, tracker) || index === task.length - 1) && (
                              <div
                                className="flex"
                                style={{
                                  padding: "16px 16px 16px 0",
                                  justifyContent: "center",
                                  margin: "auto",
                                }}
                                key={`subtasks-${objective.name}-${subTasks.join("-")}`}
                              >
                                <CryoButton
                                  task={
                                    subTasks.length > 1 ? (`${subTasks[0]} or ${subTasks[1]}` as Task) : subTasks[0]
                                  }
                                  setValue={(key, value) => setTracker({ ...tracker, [key]: value })}
                                  value={tracker[subTasks[0]] || tracker[subTasks[1]]}
                                  disabled={
                                    !task
                                      .slice(0, index)
                                      .every((priorTasks) => priorTasks.some((priorTask) => tracker[priorTask]))
                                  }
                                  hidden={isComplete(objective, tracker) && index !== task.length - 1}
                                />
                              </div>
                            ),
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Fragment>
          ))}
        {showMaps && (
          <>
            {showTitles ? (
              <h3>Maps</h3>
            ) : (
              <>
                <br />
                <br />
              </>
            )}
            <img style={{ maxWidth: "704px", marginTop: "8px" }} src="/cryo floor 1.png" alt="Delta 7 first floor" />
            <img style={{ maxWidth: "704px", marginTop: "8px" }} src="/cryo floor 2.png" alt="Delta 7 second floor" />
            <img style={{ maxWidth: "704px", marginTop: "8px" }} src="/cryo outside.png" alt="Delta 7 outside" />
          </>
        )}
      </div>
    </div>
  );
};
